import { UnmanageReasons } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { unmanageTreatment } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { getUnmanageOrDiscontinueMinDate } from 'services/treatmentHelpers';

const Title = 'Unmanage Patient Treatment';

export const UnmanageTreatmentDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    username,
    submitCallback,
}) => {
    const { treatmentId, payerId, treatmentStart, lastReferralStart } = treatmentDetails ?? {};

    const minUnmanageEffectiveDate = getUnmanageOrDiscontinueMinDate({
        treatmentStart,
        lastReferralStart,
    });

    const useMinDate = minUnmanageEffectiveDate != null;

    const minDateObject = dayjs(minUnmanageEffectiveDate)
        .startOf('day')
        .subtract(1, 'day')
        .toDate();

    const baseEffectiveDateSchema = DateInputFormik.validator()
        .required(validationStrings.requiredField.errorMessage)
        .max(dayjs().endOf('day').toDate(), 'Date cannot be in future.');

    const effectiveDateSchema = useMinDate
        ? baseEffectiveDateSchema.min(
              minDateObject,
              'Date cannot be before treatment was last active.',
          )
        : baseEffectiveDateSchema;

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                effectiveDate: effectiveDateSchema,
                ...ReasonInput.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await unmanageTreatment({
                        treatmentId,
                        payerId,
                        effectiveDate: formatDate(values.effectiveDate, DateFormats.HYPHEN_DATE),
                        username,
                        ...ReasonInput.submitValues(values),
                    });

                    submitCallback();

                    SuccessToast({ message: 'Treatment unmanaged successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to unmanage treatment. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel={Title}
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <div className="margin__bottom--medium">
                                This action will update the status of the active referral in the
                                pharmacy&apos;s view to EXPIRED. Completed performance requirements
                                will be evaluated at the end of the evaluation period, in progress
                                performance will be VOIDED if the minimum days have not been met,
                                and related tasks will be removed.
                            </div>
                            <DateInputFormik
                                id="effectiveDate"
                                label="Date To Unmanage"
                                required
                                minDate={useMinDate ? minDateObject : undefined}
                                maxDate={dayjs().endOf('day').toDate()}
                            />
                            <ReasonInput reasons={UnmanageReasons} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UnmanageTreatmentDialog.Title = Title;

UnmanageTreatmentDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
        treatmentStart: PropTypes.string,
        lastReferralStart: PropTypes.string,
    }),
    username: PropTypes.string.isRequired,
    submitCallback: PropTypes.func.isRequired,
};
